// Casca visual do campo "ff-input"/"ff-textarea"/"ffs" — DESIGN-SYSTEM.md
// §5.5 "Forms", variante A (floating label): caixa 52px, radius 12px, fundo
// `background.subtle` (surface-2), borda 1.5px sem notch (o `<legend>` do
// `fieldset` é removido — o label vive 100% dentro da caixa), label de
// repouso 13.5px `text.disabled` e, no estado encolhido (foco OU
// preenchido), 10px/800 uppercase `primary.main` a 8px do topo.
//
// É o mesmo valor já duplicado localmente em `pages/products/Form.js`,
// `pages/customers/Form.js`, `pages/services/Form.js`, `pages/users/Form.js`
// etc. (`standaloneFieldSx`) — extraído aqui para telas/modais que ainda
// caíam no default do MUI (label 16px, caixa ~56px, notch cruzando a borda).
// `theme.js` NÃO aplica isso globalmente de propósito (ver o comentário em
// `MuiInputLabel`: generalizar quebrou telas com outro layout), então cada
// formulário/modal aplica via `sx={standaloneFieldSx}` no `TextField`/
// `Select`/`Dropdown`.
//
// Cobre:
// - `TextField` de linha única e `multiline` (textarea, `minHeight` 96px);
// - `Dropdown`/`Select` com `labelVariant="static-micro"` (label estático
//   acima da caixa, valor centralizado — `.ffs` do protótipo);
// - adornos de fim (ex.: botão de mostrar senha em `InputAdornment`).
// Não use com `Autocomplete` multi-seleção (chips) sem conferir a altura.
export const standaloneFieldSx = {
    '& .MuiInputBase-input': { fontSize: '13.5px' },
    // Estado normal: `top:17px` do `.ff-label`; `scale(1)` porque o
    // `fontSize` já é o tamanho real (não um valor a ser escalado).
    '& .MuiInputLabel-root': {
        fontSize: '13.5px',
        color: 'text.disabled',
        transform: 'translate(14px, 17px) scale(1)',
    },
    // Estado "encolhido" (foco OU preenchido): `top:8px` do `.ff-label`,
    // continua DENTRO da caixa (o `translate(14px, -9px)` do tema, pensado
    // para o notch, é sobrescrito aqui).
    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
        fontSize: '10px',
        fontWeight: 800,
        color: 'primary.main',
        letterSpacing: '.04em',
        textTransform: 'uppercase',
        transform: 'translate(14px, 8px) scale(1)',
    },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: 'background.subtle',
        height: '52px',
        transition: 'background-color .15s ease, box-shadow .15s ease',
        '&.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: '96px',
            alignItems: 'flex-start',
            // `.ff-textarea{padding-top:24px}` — reserva fixa no topo para o
            // valor não colidir com o label encolhido (sem notch nativo).
            padding: '24px 14px 7px 14px',
        },
    },
    // `.ff-input{padding:21px 14px 7px}` — `<input>` de linha única. Exclui
    // `.MuiSelect-select` (label do `Dropdown` é estático, fora da caixa).
    '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
        padding: '21px 14px 7px 14px',
    },
    // `.ffs select{height:52px; padding:0 14px}` — valor do `<Select>`
    // centralizado verticalmente. 32px à direita reservam o espaço da seta
    // (senão valor longo passa por baixo dela).
    '& .MuiSelect-select.MuiOutlinedInput-input': {
        padding: '0 32px 0 14px',
        display: 'flex',
        alignItems: 'center',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderWidth: '1.5px',
        borderColor: 'divider',
    },
    // Sem o `legend` a borda fica sempre fechada (retângulo contínuo).
    '& .MuiOutlinedInput-notchedOutline legend': {
        display: 'none',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
        borderWidth: '1.5px',
    },
    '& .MuiInputBase-multiline textarea': {
        resize: 'vertical',
    },
};

// Variante para `<TextField select>` com label flutuante (não `Dropdown`
// static-micro): o valor selecionado fica ABAIXO do label encolhido (mesmo
// padding do `<input>`) em vez de centralizado — senão colide com o label.
export const standaloneFloatingSelectSx = {
    ...standaloneFieldSx,
    '& .MuiSelect-select.MuiOutlinedInput-input': {
        padding: '21px 32px 7px 14px',
        display: 'block',
    },
};
